/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Site } from '../types/Site';

interface SearchLocation {
  lat: number;
  lon: number;
  zoom?: number;
  showAllSites?: boolean;
  selectedSiteId?: string;
}

interface LocationSearchProps {
  onSearch: (location: SearchLocation) => void;
  onSiteSelect?: (site: Site) => void;
  sites: Site[];
  isLoading: boolean;
  onFilterClick?: () => void;
  onClearFilters?: () => void;
  hasActiveFilters?: boolean;
  onAccountSettings?: () => void;
  onAdminPanel?: () => void;
  onChangeAssetType?: () => void;
  isAdmin?: boolean;
}

interface SearchResult {
  type: 'location' | 'site';
  data: {
    lat: number;
    lon: number;
    display_name: string;
  } | Site;
}

const SearchContainer = styled.div`
  position: relative;
  flex-grow: 1;
  min-width: 0;
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  z-index: 1000;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 100%;
  font-size: 14px;
  outline: none;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  transition: all 0.2s ease;
  box-sizing: border-box;
  position: relative;
  z-index: 1001;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
  }

  &:focus {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.3);
  }

  @media (max-width: 640px) {
    padding: 10px 12px;
    font-size: 14px;
    border-radius: 6px;
    height: 44px;
  }
`;

const ResultsContainer = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  background: #1a1a1a;
  border-radius: 12px;
  box-shadow: 0 0 32px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 1002;
  bottom: 144px; // Adjusted back down by 40px
  max-height: calc(100vh - 260px); // Adjusted to account for new position

  @media (min-width: 641px) {
    left: 50%;
    transform: translateX(-50%);
    width: 480px; // Fixed width on desktop for better readability
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 640px) {
    bottom: 70px;
    max-height: calc(100vh - 180px);
    margin: 0 10px;
    width: calc(100% - 20px);
  }
`;

const ResultsHeader = styled.div`
  padding: 12px 16px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.05);

  @media (max-width: 640px) {
    padding: 10px 12px;
    font-size: 14px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  padding: 4px;
  cursor: pointer;
  display: block; // Always show close button
  font-size: 18px;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const ResultsSection = styled.div`
  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const SectionHeader = styled.div`
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.05em;

  @media (max-width: 640px) {
    padding: 8px 12px;
    font-size: 11px;
  }
`;

const ResultsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
  max-height: calc(100vh - 340px); // Adjusted for the new position
`;

const ResultItem = styled.li`
  padding: 12px 16px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.9);
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  .location-name {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .location-details {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  @media (max-width: 640px) {
    padding: 10px 12px;
    font-size: 13px;
    gap: 8px;

    .location-details {
      font-size: 11px;
    }
  }
`;

const LocationIcon = styled.span`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2ea043;
  color: white;
  border-radius: 6px;
  font-size: 14px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    width: 24px;
    height: 24px;
    font-size: 12px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 6px;
  position: relative;
  z-index: 1001;

  @media (max-width: 640px) {
    gap: 6px;
    margin-top: 6px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 4px;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera*/
    }
  }
`;

const QuickActionButton = styled.button<{ isActive?: boolean; variant?: 'filter' | 'clear' | 'account' | 'admin' | 'region' }>`
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
  
  /* Color variants based on button type */
  background: ${props => {
    if (props.variant === 'filter') {
      return props.isActive ? '#2ea043' : 'rgba(46, 160, 67, 0.15)';
    } else if (props.variant === 'clear') {
      return 'rgba(239, 68, 68, 0.15)';
    } else if (props.variant === 'account') {
      return 'rgba(59, 130, 246, 0.15)';
    } else if (props.variant === 'admin') {
      return 'rgba(124, 58, 237, 0.15)'; // Purple for admin
    } else if (props.variant === 'region') {
      return 'rgba(255, 255, 255, 0.15)';
    } else {
      return props.isActive ? '#2ea043' : 'rgba(255, 255, 255, 0.1)';
    }
  }};
  
  color: ${props => {
    if (props.variant === 'filter') {
      return props.isActive ? '#fff' : '#2ea043';
    } else if (props.variant === 'clear') {
      return '#ef4444';
    } else if (props.variant === 'account') {
      return '#3b82f6';
    } else if (props.variant === 'admin') {
      return '#7c3aed'; // Purple for admin
    } else if (props.variant === 'region') {
      return '#2ea043';
    } else {
      return props.isActive ? '#fff' : 'rgba(255, 255, 255, 0.9)';
    }
  }};

  &:hover {
    background: ${props => {
      if (props.variant === 'filter') {
        return props.isActive ? '#2ea043' : 'rgba(46, 160, 67, 0.25)';
      } else if (props.variant === 'clear') {
        return 'rgba(239, 68, 68, 0.25)';
      } else if (props.variant === 'account') {
        return 'rgba(59, 130, 246, 0.25)';
      } else if (props.variant === 'admin') {
        return 'rgba(124, 58, 237, 0.25)'; // Purple for admin
      } else if (props.variant === 'region') {
        return 'rgba(46, 160, 67, 0.25)'; // Green for region, matches filter
      } else {
        return props.isActive ? '#2ea043' : 'rgba(255, 255, 255, 0.15)';
      }
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    padding: 8px 12px;
    font-size: 13px;
    gap: 6px;
    flex-shrink: 0;
  }
`;

const SearchOverlay = styled.div<{ show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1001;
  display: ${props => props.show ? 'block' : 'none'};
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  position: absolute;
  bottom: -24px;
  left: 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.95);
  padding: 4px 8px;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &::before {
    content: '⚠️';
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  margin-top: 8px;
  color: #2196f3;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &::before {
    content: '🔄';
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LocationSearch = ({ 
  onSearch, 
  onSiteSelect,
  sites,
  isLoading, 
  onFilterClick, 
  onClearFilters,
  hasActiveFilters,
  onAccountSettings,
  onAdminPanel,
  onChangeAssetType,
  isAdmin = false
}: LocationSearchProps): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [lastClickedSite, setLastClickedSite] = useState<string | null>(null);
  const searchTimeout = useRef<NodeJS.Timeout>();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTerm.trim().length < 2) {
      setResults([]);
      return;
    }

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      setError(null);

      try {
        const searchTermUpper = searchTerm.toUpperCase();
        const isSiteCode = searchTermUpper.length === 4 && /^[A-Z0-9]{4}$/.test(searchTermUpper);
        
        // Search for sites first
        let siteResults = sites.filter(site => {
          if (isSiteCode) {
            // If it's a 4-character code, only match exact site codes
            return site.name.siteCode === searchTermUpper;
          } else {
            // Otherwise search in station names
            return (
              site.name.stationName.toLowerCase().includes(searchTerm.toLowerCase()) ||
              site.name.siteCode.includes(searchTermUpper)
            );
          }
        }).map(site => ({
          type: 'site' as const,
          data: site
        }));

        // If we have an exact match for a site code, only show that result
        const exactSiteCodeMatch = isSiteCode && siteResults.some(result => 
          (result.data as Site).name.siteCode === searchTermUpper
        );

        if (exactSiteCodeMatch) {
          siteResults = siteResults.filter(result => 
            (result.data as Site).name.siteCode === searchTermUpper
          );
        }

        // Only search locations if no exact site code match is found
        let locationResults: SearchResult[] = [];
        if (!exactSiteCodeMatch) {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?` +
            `format=json&` +
            `q=${encodeURIComponent(searchTerm)}` +
            `&countrycodes=gb` +
            `&addressdetails=1` +
            `&limit=5` +
            `&featuretype=city,town,village,suburb,hamlet,locality,neighbourhood`
          );

          if (!response.ok) {
            throw new Error('Failed to search location');
          }

          const locationData = await response.json();
          locationResults = locationData
            .filter((result: any) => (
              result.importance > 0.2 ||
              ['city', 'town', 'village', 'suburb', 'hamlet'].includes(result.type)
            ))
            .map((result: any) => ({
              type: 'location' as const,
              data: {
                lat: parseFloat(result.lat),
                lon: parseFloat(result.lon),
                display_name: formatDisplayName(result)
              }
            }));
        }

        // Combine and sort results
        const combinedResults = [...siteResults, ...locationResults];
        setResults(combinedResults);
        setShowResults(Boolean(combinedResults.length));
      } catch (err) {
        setError('Error searching');
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 150);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchTerm, sites]);

  const formatDisplayName = (result: any): string => {
    if (!result.address) {
      const parts = result.display_name.split(', ');
      return parts.slice(0, 2).join(', ');
    }

    const {
      village, town, city, county
    } = result.address;

    const components = [];
    if (village) components.push(village);
    else if (town) components.push(town);
    else if (city) components.push(city);
    if (county && components.length < 2) components.push(county);

    return components.join(', ');
  };

  const handleResultClick = (result: SearchResult) => {
    if (result.type === 'location') {
      // For locations, just search and close
      const locationData = result.data as { lat: number; lon: number; display_name: string };
      setSearchTerm(locationData.display_name);
      onSearch({ 
        lat: locationData.lat, 
        lon: locationData.lon,
        zoom: 13,
        showAllSites: true
      });
      setLastClickedSite(null);
      setShowResults(false);
    } else {
      const site = result.data as Site;
      setSearchTerm(site.name.stationName);
      
      // Check if this is a second click on the same site
      const isSameAsPrevious = lastClickedSite === site.name.siteCode;
      
      // Always update the map to center on the site
      onSearch({ 
        lat: site.coordinates.lat, 
        lon: site.coordinates.lng,
        zoom: 18,
        showAllSites: false,
        selectedSiteId: site.name.siteCode
      });
      
      // On second click, show the site details
      if (isSameAsPrevious && onSiteSelect) {
        onSiteSelect(site);
        setShowResults(false);
      } else {
        // On first click, just filter to show this site
        setLastClickedSite(site.name.siteCode);
        setResults([result]);
        setShowResults(true);
      }
    }
  };

  const handleClearAll = () => {
    setSearchTerm('');
    setResults([]);
    setShowResults(false);
    setLastClickedSite(null);
    if (onClearFilters) {
      onClearFilters();
    }
  };

  const locationResults = results.filter(result => result.type === 'location');
  const siteResults = results.filter(result => result.type === 'site');

  return (
    <SearchContainer ref={containerRef}>
      <SearchWrapper>
        <SearchInput
          type="text"
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          placeholder="Search location or site..."
          onFocus={() => setShowResults(true)}
        />
        <SearchOverlay 
          show={showResults && results.length > 0} 
          onClick={() => setShowResults(false)}
        />
        {showResults && results.length > 0 && (
          <ResultsContainer>
            <ResultsHeader>
              <span>Search Results</span>
              <CloseButton onClick={() => setShowResults(false)}>✕</CloseButton>
            </ResultsHeader>
            <ResultsList>
              {locationResults.length > 0 && (
                <ResultsSection>
                  <SectionHeader>
                    GPS Locations
                  </SectionHeader>
                  {locationResults.map((result, index) => (
                    <ResultItem key={`location-${index}`} onClick={() => handleResultClick(result)}>
                      <LocationIcon>📍</LocationIcon>
                      <div>
                        <div className="location-name">
                          {(result.data as { display_name: string }).display_name.split(',')[0]}
                        </div>
                        <div className="location-details">
                          {(result.data as { display_name: string }).display_name.split(',').slice(1, 3).join(',')}
                        </div>
                      </div>
                    </ResultItem>
                  ))}
                </ResultsSection>
              )}
              {siteResults.length > 0 && (
                <ResultsSection>
                  <SectionHeader>
                    Sites
                  </SectionHeader>
                  {siteResults.map((result, index) => (
                    <ResultItem key={`site-${index}`} onClick={() => handleResultClick(result)}>
                      <LocationIcon>🏢</LocationIcon>
                      <div>
                        <div className="location-name">
                          {(result.data as Site).name.stationName}
                        </div>
                        <div className="location-details">
                          {(result.data as Site).name.siteCode}
                        </div>
                      </div>
                    </ResultItem>
                  ))}
                </ResultsSection>
              )}
            </ResultsList>
          </ResultsContainer>
        )}
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SearchWrapper>
      <ButtonGroup>
        <QuickActionButton 
          onClick={onFilterClick}
          isActive={hasActiveFilters}
          variant="filter"
        >
          Filter
        </QuickActionButton>
        <QuickActionButton 
          onClick={handleClearAll}
          disabled={!hasActiveFilters && !searchTerm}
          variant="clear"
        >
          Clear
        </QuickActionButton>
        {onChangeAssetType && (
          <QuickActionButton 
            onClick={onChangeAssetType}
            variant="region"
          >
            Main Menu
          </QuickActionButton>
        )}
        <QuickActionButton 
          onClick={onAccountSettings}
          variant="account"
        >
          Account
        </QuickActionButton>
        {isAdmin && onAdminPanel && (
          <QuickActionButton 
            onClick={onAdminPanel}
            variant="admin"
          >
            Admin
          </QuickActionButton>
        )}
      </ButtonGroup>
    </SearchContainer>
  );
};

export default LocationSearch; 